'use client'

import dynamic from 'next/dynamic'
const Dialog = dynamic(
	() => import('@qr-front/ui/components/Dialog').then((mod) => mod.Dialog),
	{ ssr: false },
)
const DialogContent = dynamic(
	() =>
		import('@qr-front/ui/components/Dialog').then((mod) => mod.DialogContent),
	{ ssr: false },
)

import { LineCrown } from '@qr-front/icons/line-crown'
import { Button } from '@qr-front/ui/components/Button'
import { ROUTES } from 'constants/routesConstants'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { UserStatusEnum } from 'types/subscription'

import { userAtom } from '@/global-state'
import { Link } from '@/i18n-routing'

import { useShowTrialCountdown } from '../../hooks/useShowTrialCountdown'
import { subExpiredDialogAtom } from '../dialogStates'
import { SubExpiredRestrictedFeatures } from './SubExpiredRestrictedFeatures'
import { TrialCountdown } from './TrialCountdown'

export const SubExpiredDialog = () => {
	const t = useTranslations()
	const [open, setOpen] = useAtom(subExpiredDialogAtom)

	const user = useAtomValue(userAtom)
	const userStatus = user?.status

	let dialogTitle = 'dashboard__trial_expired__modal_title'
	let showCountdown = true

	switch (userStatus) {
		case UserStatusEnum.PREMIUM_CANCELLING:
			dialogTitle = 'dashboard__user_status__is_premium_cancelling__modal_title'
			showCountdown = false
			break
		case UserStatusEnum.PREMIUM_CANCELLED:
			dialogTitle = 'dashboard__user_status__is_premium_cancelled__modal_title'
			showCountdown = false
			break
		case UserStatusEnum.PREMIUM_UNPAID:
		case UserStatusEnum.PREMIUM_IN_DEBT:
			dialogTitle = 'dashboard__user_status__is_premium_unpaid__modal_title'
			showCountdown = false
			break
		case UserStatusEnum.DEACTIVATED:
			dialogTitle = 'dashboard__user_status__is_deactivated__modal_title'
			showCountdown = false
	}

	const trialCountDown = useShowTrialCountdown(user, showCountdown)

	if (!open || !user) {
		return null
	}

	const countdown = (
		<div className="flex gap-8">
			{trialCountDown && <TrialCountdown trialCountDown={trialCountDown} />}
		</div>
	)

	const plansButton = (
		<Link href={ROUTES.plans} className="tablet:flex">
			<Button
				visual="default"
				className="type-body-small min-w-[210px]"
				pulsate
			>
				<LineCrown className="size-4" />
				{t('dashboard__shared__upgrade_cta_button')}
			</Button>
		</Link>
	)

	const upgradeTitle =
		userStatus === UserStatusEnum.PREMIUM_CANCELLED
			? t('dashboard__trial_expired__modal_upgrade_title__premium_canceled')
			: t('dashboard__trial_expired__modal_upgrade_title')

	return (
		<>
			<Dialog open={open} onOpenChange={() => setOpen(!open)}>
				<DialogContent
					closeButtonLabel={t('ui__popup__close_button')}
					className="my-4 max-h-[calc(100dvh-2rem)] px-0 pb-4 tablet:my-0 tablet:mt-0 tablet:max-h-[calc(85dvh-4rem)] tablet:px-6 tablet:pb-6"
				>
					{/* Mobile: sticky button layout */}
					<div className="flex flex-col tablet:hidden">
						<div className="flex flex-col items-center justify-center gap-4 p-4 pt-0">
							<div className="flex flex-col items-center justify-center gap-4 p-2">
								<h3 className="type-heading-3 text-center">{t(dialogTitle)}</h3>
								{showCountdown ? countdown : null}
							</div>
							<SubExpiredRestrictedFeatures trialCountDown={trialCountDown} />
							<h4 className="type-body-medium text-center text-dark-grey">
								{upgradeTitle}
							</h4>
						</div>
						<div className="sticky bottom-0 left-0 right-0 flex items-center justify-center overflow-visible bg-white p-4 pb-4 pt-2">
							<Link href={ROUTES.plans}>
								<Button
									visual="default"
									className="type-body-small w-full"
									pulsate
								>
									<LineCrown className="size-4" />
									{t('dashboard__shared__upgrade_cta_button')}
								</Button>
							</Link>
						</div>
					</div>

					{/* Tablet+: original layout */}
					<div className="hidden flex-col items-center justify-center gap-4 tablet:flex tablet:gap-6">
						<div className="flex flex-col items-center justify-center gap-4 p-2">
							<h3 className="type-heading-3 text-center">{t(dialogTitle)}</h3>
							{showCountdown ? countdown : null}
						</div>
						<SubExpiredRestrictedFeatures trialCountDown={trialCountDown} />
						<h4 className="type-body-medium text-center text-dark-grey">
							{upgradeTitle}
						</h4>
						{plansButton}
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
